import { useState, useRef, useCallback, useEffect, KeyboardEvent } from 'react'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { useFocusMethod } from '@/hooks/useFocusMethod'

interface DateFieldProps {
	value?: string // ISO date string YYYY-MM-DD
	onValueChange?: (value: string) => void
	onClear?: () => void
	label?: string
	hideLabel?: boolean
	minDate?: string // ISO YYYY-MM-DD — dates before this are clamped on entry
	maxDate?: string // ISO YYYY-MM-DD — dates after this are clamped on entry
	disabled?: boolean
	spacesClass?: string
	className?: string
}

function clampSegment (
	v: string,
	min: number,
	max: number,
	padLength: number
): string {
	const num = parseInt(v, 10)
	const clamped = Math.min(Math.max(num, min), max)
	return String(clamped).padStart(padLength, '0')
}

function getDaysInMonth (month: string, year: string): number {
	const m = parseInt(month, 10)
	if (!m || m < 1 || m > 12) return 31
	const y = parseInt(year, 10)
	// new Date(y, m, 0) returns the last day of month m for year y
	// Use a known non-leap and leap year proxy when year is incomplete
	const resolvedYear = y >= 1000 ? y : 2001
	return new Date(resolvedYear, m, 0).getDate()
}

function DateSegment ({
	value,
	placeholder,
	maxLength,
	min = 1,
	max,
	disabled,
	onValueChange,
	onNext,
	onPrev,
	inputRef
}: {
	value: string
	placeholder: string
	maxLength: number
	min?: number
	max?: number
	disabled?: boolean
	onValueChange: (v: string) => void
	onNext?: () => void
	onPrev?: () => void
	inputRef?: React.RefObject<HTMLInputElement | null>
}) {
	// Track the latest committed value synchronously so handleBlur
	// doesn't read the stale prop when blur fires before React re-renders.
	const latestValue = useRef(value)

	// Guard flag: when true, the next mouseup preventDefault preserves
	// the selection made in onFocus (browsers deselect on mouseup).
	const justFocused = useRef(false)
	useEffect(() => {
		latestValue.current = value
	}, [value])

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowRight' || (e.key === '/' && onNext)) {
			onNext?.()
			e.preventDefault()
		} else if (e.key === 'ArrowLeft' && onPrev) {
			onPrev()
			e.preventDefault()
		}
	}

	const handleBlur = () => {
		const current = latestValue.current
		if (!current || max === undefined) return
		const padded = current.padStart(maxLength, '0')
		const clamped = clampSegment(padded, min, max, maxLength)
		if (clamped !== current) onValueChange(clamped)
	}

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		justFocused.current = true
		e.currentTarget.select()
	}

	const handleMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
		if (justFocused.current) {
			justFocused.current = false
			e.preventDefault()
		}
	}

	return (
		<input
			ref={inputRef}
			type='text'
			inputMode='numeric'
			value={value}
			placeholder={placeholder}
			maxLength={maxLength}
			onFocus={handleFocus}
			onMouseUp={handleMouseUp}
			onChange={e => {
				const v = e.target.value.replace(/\D/g, '')
				if (v.length === maxLength && max !== undefined) {
					const clamped = clampSegment(v, min, max, maxLength)
					latestValue.current = clamped
					onValueChange(clamped)
					onNext?.()
				} else {
					latestValue.current = v
					onValueChange(v)
				}
			}}
			onBlur={handleBlur}
			onKeyDown={handleKeyDown}
			className={`date-segment spaces:p-4 spaces:rounded-8 spaces:text-16/24 spaces:font-default ${
				disabled ? 'spaces:text-content-3' : 'spaces:text-content-1'
			} spaces:bg-transparent spaces:outline-none spaces:text-center spaces:placeholder:text-content-3`}
			style={{ width: `calc(${maxLength + 0.5}ch + 12px)` }}
		/>
	)
}

export function DateField ({
	value,
	onValueChange,
	onClear,
	label,
	hideLabel = false,
	minDate,
	maxDate,
	disabled = false,
	spacesClass = '',
	className = ''
}: DateFieldProps) {
	const [month, setMonth] = useState(() =>
		value ? value.split('-')[1] ?? '' : ''
	)
	const [day, setDay] = useState(() => (value ? value.split('-')[2] ?? '' : ''))
	const [year, setYear] = useState(() =>
		value ? value.split('-')[0] ?? '' : ''
	)

	useEffect(() => {
		if (value) {
			const [y, m, d] = value.split('-')
			setYear(y ?? '')
			setMonth(m ?? '')
			setDay(d ?? '')
		} else {
			setYear('')
			setMonth('')
			setDay('')
		}
	}, [value])

	const monthRef = useRef<HTMLInputElement>(null)
	const dayRef = useRef<HTMLInputElement>(null)
	const yearRef = useRef<HTMLInputElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)

	useFocusMethod(wrapperRef)

	const emit = useCallback(
		(m: string, d: string, y: string) => {
			if (m.length === 2 && d.length === 2 && y.length === 4) {
				let date = `${y}-${m}-${d}`
				const bound = minDate && date < minDate
					? minDate
					: maxDate && date > maxDate
						? maxDate
						: null
				if (bound) {
					date = bound
					const [cy, cm, cd] = bound.split('-')
					setMonth(cm)
					setDay(cd)
					setYear(cy)
				}
				onValueChange?.(date)
			}
		},
		[onValueChange, minDate, maxDate]
	)

	const hasValue = !!(month || day || year)
	const rootClasses = ['spaces-date-field spaces:flex spaces:flex-col spaces:gap-8', spacesClass, className]
		.filter(Boolean)
		.join(' ')
	const wrapperClasses = [
		'field-wrapper spaces:flex spaces:items-center spaces:rounded-12 spaces:border spaces:border-canvas-2',
		'spaces:pl-7 spaces:pr-4 spaces:py-3',
		disabled && 'spaces-disabled',
		disabled ? 'spaces:bg-canvas-4 spaces:cursor-not-allowed' : 'spaces:bg-canvas-base'
	]
		.filter(Boolean)
		.join(' ')
	const segmentsWrapperClasses = [
		'spaces:flex spaces:flex-1 spaces:items-center spaces:overflow-visible',
		disabled && 'spaces:pointer-events-none'
	]
		.filter(Boolean)
		.join(' ')

	const handleClear = useCallback(() => {
		setMonth('')
		setDay('')
		setYear('')
		onValueChange?.('')
		onClear?.()
	}, [onValueChange, onClear])

	return (
		<div className={rootClasses}>
			{label && !hideLabel && (
				<label className='spaces:text-14/16 spaces:font-default spaces:text-content-2'>{label}</label>
			)}
			<div
				ref={wrapperRef}
				aria-label={hideLabel && label ? label : undefined}
				className={wrapperClasses}
			>
				{/*/ Segments wrapper */}
				<div className={segmentsWrapperClasses}>
					<DateSegment
						inputRef={monthRef}
						value={month}
						placeholder='MM'
						maxLength={2}
						min={1}
						max={12}
						disabled={disabled}
						onValueChange={v => {
							setMonth(v)
							emit(v, day, year)
						}}
						onNext={() => dayRef.current?.focus()}
					/>
					<span className='spaces:text-16/24 spaces:text-content-3 spaces:shrink-0'>/</span>
					<DateSegment
						inputRef={dayRef}
						value={day}
						placeholder='DD'
						maxLength={2}
						min={1}
						max={getDaysInMonth(month, year)}
						disabled={disabled}
						onValueChange={v => {
							setDay(v)
							emit(month, v, year)
						}}
						onPrev={() => monthRef.current?.focus()}
						onNext={() => yearRef.current?.focus()}
					/>
					<span className='spaces:text-16/24 spaces:text-content-3 spaces:shrink-0'>/</span>
					<DateSegment
						inputRef={yearRef}
						value={year}
						placeholder='YYYY'
						maxLength={4}
						disabled={disabled}
						onValueChange={v => {
							setYear(v)
							emit(month, day, v)
						}}
						onPrev={() => dayRef.current?.focus()}
					/>
				</div>
				{hasValue && !disabled && (
					<Button
						appearance='ghost'
						color='content3'
						size='sm'
						iconOnly='X'
						type='button'
						aria-label='Clear date'
						onClick={handleClear}
					/>
				)}
				{disabled && (
					<div className='spaces:flex spaces:items-center spaces:p-8'>
						<Icon name='Lock' size={16} className='spaces:text-content-3' />
					</div>
				)}
			</div>
		</div>
	)
}
